import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import StudentSubjects from "@/components/StudentSubjects";
import StudentDetailsEditor from "@/components/StudentDetailsEditor";
import { ArrowLeft, Save, Calendar, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  gender: string | null;
  office_name: string;
  calling_name: string | null;
  passport_number: string | null;
  class_1_medical: string;
  class_2_medical: string;
  date_of_joining: string;
  notes: string | null;
  batch_id: string | null;
  computer_number: string | null;
  egca_id: string | null;
  computer_number_status: string;
}

const StudentDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [student, setStudent] = useState<Student | null>(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [attendanceStats, setAttendanceStats] = useState({ present: 0, absent: 0, total: 0 });
  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }

      const { data: isAdminUser } = await supabase.rpc('has_role', {
        _user_id: session.user.id,
        _role: 'admin'
      });
      const { data: isManagerUser } = await supabase.rpc('has_role', {
        _user_id: session.user.id,
        _role: 'manager'
      });
      setIsAdmin(isAdminUser || isManagerUser || false);
    };
    checkAuth();
    fetchStudent();
    fetchAttendanceStats();

    // Set up real-time subscription for attendance updates
    const attendanceChannel = supabase
      .channel(`student_attendance_${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'student_attendance',
          filter: `student_id=eq.${id}`
        },
        () => {
          fetchAttendanceStats();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(attendanceChannel);
    };
  }, [navigate, id, dateFrom, dateTo]);

  const fetchStudent = async () => {
    try {
      const { data, error } = await supabase
        .from("students")
        .select("*")
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        toast.error("Student not found");
        navigate("/students");
        return;
      }
      
      setStudent(data);
      setNotes(data.notes || "");

      // Fetch profile photo if available
      if (data.profile_photo_url) {
        const { data: urlData } = await supabase.storage
          .from('student-documents')
          .createSignedUrl(data.profile_photo_url, 3600);
        
        if (urlData?.signedUrl) {
          setProfilePhotoUrl(urlData.signedUrl);
        }
      }
    } catch (error) {
      console.error("Error fetching student:", error);
      toast.error("Failed to load student details");
      navigate("/students");
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendanceStats = async () => {
    try {
      let query = supabase
        .from("student_attendance")
        .select("status, date")
        .eq("student_id", id);

      // Apply date range filters if set
      if (dateFrom) {
        query = query.gte("date", dateFrom);
      }
      if (dateTo) {
        query = query.lte("date", dateTo);
      }

      const { data, error } = await query;

      if (error) throw error;

      const present = data?.filter(record => record.status === "Present").length || 0;
      const absent = data?.filter(record => record.status === "Absent").length || 0;
      const total = data?.length || 0;

      setAttendanceStats({ present, absent, total });
    } catch (error) {
      console.error("Error fetching attendance stats:", error);
    }
  };

  const handleSaveNotes = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("students")
        .update({ notes: notes.trim() || null })
        .eq("id", id);

      if (error) throw error;
      toast.success("Notes saved successfully!");
    } catch (error: any) {
      toast.error(error.message || "Failed to save notes");
    } finally {
      setSaving(false);
    }
  };

  const getMedicalBadgeVariant = (status: string) => {
    switch (status) {
      case "Done":
      case "Test Done":
        return "default";
      case "Pending":
        return "secondary";
      case "Expired":
        return "destructive";
      case "Retest":
        return "outline";
      default:
        return "outline";
    }
  };

  if (loading) {
    return (
      <Layout userName={userName}>
        <div className="text-center py-8">Loading student details...</div>
      </Layout>
    );
  }

  if (!student) {
    return (
      <Layout userName={userName}>
        <div className="text-center py-8">Student not found</div>
      </Layout>
    );
  }

  return (
    <Layout userName={userName}>
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate("/students")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Students
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start gap-4">
              {profilePhotoUrl && (
                <img 
                  src={profilePhotoUrl} 
                  alt={`${student.first_name} ${student.last_name || ''}`}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover border-2 border-border"
                />
              )}
              <div className="flex-1">
                <CardTitle className="text-xl sm:text-2xl">
                  {student.calling_name || student.office_name}
                </CardTitle>
                <p className="text-muted-foreground mt-1">
                  {student.first_name} {student.last_name || ''}
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{student.email || "Not provided"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Phone</Label>
                <p className="font-medium">{student.phone || "Not provided"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Gender</Label>
                <p className="font-medium">{student.gender || "Not provided"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Calling Name</Label>
                <p className="font-medium">{student.calling_name || student.office_name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Passport/ID Number</Label>
                <p className="font-medium">{student.passport_number || "Not provided"}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Date of Joining</Label>
                <p className="font-medium">{new Date(student.date_of_joining).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-4">
              <Badge 
                variant={getMedicalBadgeVariant(student.class_1_medical)}
                className={student.class_1_medical === "Pending" ? "bg-red-500 text-white hover:bg-red-600" : ""}
              >
                Class 1 Medical: {student.class_1_medical}
              </Badge>
              <Badge 
                variant={getMedicalBadgeVariant(student.class_2_medical)}
                className={student.class_2_medical === "Pending" ? "bg-red-500 text-white hover:bg-red-600" : ""}
              >
                Class 2 Medical: {student.class_2_medical}
              </Badge>
              <Badge 
                variant="outline"
                className={student.computer_number_status === "Pending" ? "bg-red-500 text-white border-red-500" : ""}
              >
                Computer Number: {student.computer_number_status}
              </Badge>
              {student.egca_id && (
                <Badge variant="outline">
                  EGCA ID: {student.egca_id}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <CardTitle className="text-lg sm:text-xl flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Attendance Summary
              </CardTitle>
              <div className="flex flex-wrap gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Calendar className="h-4 w-4" />
                      {dateFrom || dateTo ? "Filter Applied" : "Filter by Date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 pointer-events-auto" align="end">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium mb-2">Date Range</Label>
                        <p className="text-xs text-muted-foreground mb-3">
                          Filter attendance records by date range
                        </p>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="date-from" className="text-xs">From</Label>
                          <Input
                            id="date-from"
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            max={dateTo || new Date().toISOString().split('T')[0]}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="date-to" className="text-xs">To</Label>
                          <Input
                            id="date-to"
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            min={dateFrom}
                            max={new Date().toISOString().split('T')[0]}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button 
                          size="sm" 
                          onClick={() => fetchAttendanceStats()}
                          className="flex-1"
                        >
                          Apply Filter
                        </Button>
                        {(dateFrom || dateTo) && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setDateFrom("");
                              setDateTo("");
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(dateFrom || dateTo) && (
              <div className="mb-4 p-3 bg-muted/50 rounded-lg border">
                <p className="text-sm text-muted-foreground">
                  Showing attendance from{" "}
                  <span className="font-medium text-foreground">
                    {dateFrom ? new Date(dateFrom).toLocaleDateString() : "start"}
                  </span>
                  {" "}to{" "}
                  <span className="font-medium text-foreground">
                    {dateTo ? new Date(dateTo).toLocaleDateString() : "present"}
                  </span>
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-green-50 dark:bg-green-950/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
                <Label className="text-muted-foreground text-sm">Days Present</Label>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">{attendanceStats.present}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-950/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                <Label className="text-muted-foreground text-sm">Days Absent</Label>
                <p className="text-2xl font-bold text-red-600 dark:text-red-400">{attendanceStats.absent}</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                <Label className="text-muted-foreground text-sm">Total Days</Label>
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{attendanceStats.total}</p>
              </div>
            </div>
            {attendanceStats.total > 0 && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Attendance Rate: <span className="font-semibold text-foreground">
                    {((attendanceStats.present / attendanceStats.total) * 100).toFixed(1)}%
                  </span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        <StudentDetailsEditor studentId={id!} />

        <StudentSubjects studentId={id!} />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Progress Notes</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="notes">Track student progress and notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Add notes about student progress, achievements, areas for improvement..."
                rows={8}
                className="resize-none"
              />
            </div>
            <Button onClick={handleSaveNotes} disabled={saving} className="w-full sm:w-auto">
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Notes"}
            </Button>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default StudentDetails;
